import { AlertTriangle, Coins, Layers3, Users } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@ncoder/ui';
import type { OrgUsageReport, OrgUsageRollup } from '@/services/org-admin';
import { formatCurrencyUsd, formatTokenCount, isOverBudget } from './usageFormat';

export interface OrgUsageSummaryPanelProps {
  isAdmin: boolean;
  periodLabel: string;
  rollup?: OrgUsageRollup;
  report?: OrgUsageReport;
  isLoading: boolean;
}

const PARTIAL_SOURCE_LABEL: Record<string, string> = {
  customers: 'spend and budget figures',
  spend_logs: 'token counts',
};

function StatTile({
  icon: Icon,
  label,
  value,
  warn,
}: {
  icon: typeof Coins;
  label: string;
  value: string;
  warn?: boolean;
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Icon className={`h-3.5 w-3.5 ${warn ? 'text-warning' : 'text-primary'}`} />
        {label}
      </div>
      <div className={`mt-1 text-xl font-semibold ${warn ? 'text-warning' : 'text-foreground'}`}>{value}</div>
    </div>
  );
}

function SkeletonStatTile() {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="h-3 w-20 rounded bg-muted-foreground/10 loading-parallax" />
      <div className="mt-2 h-6 w-16 rounded-md bg-muted-foreground/10 loading-parallax" />
    </div>
  );
}

export function OrgUsageSummaryPanel({ isAdmin, periodLabel, rollup, report, isLoading }: OrgUsageSummaryPanelProps) {
  const blockedOrOverBudgetCount =
    report?.members.filter((m) => m.blocked || isOverBudget(m.spend_usd_cycle, m.budget_usd)).length ?? 0;

  return (
    <div className="studio-card p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Coins className="h-4 w-4" />
        </div>
        <div>
          <div className="text-xs font-semibold uppercase text-muted-foreground">
            {isAdmin ? `Org usage — ${periodLabel}` : 'Org usage'}
          </div>
          <p className="text-xs text-muted-foreground">
            {isAdmin ? 'Spend, tokens and budget health across the org.' : 'Spend rollup for your organization.'}
          </p>
        </div>
      </div>

      {!isLoading && report && report.partial.length > 0 && (
        <Alert className="mt-4 border-warning/30 bg-warning/10 text-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Some numbers below may be stale</AlertTitle>
          <AlertDescription>
            The usage service could not return{' '}
            {report.partial.map((source) => PARTIAL_SOURCE_LABEL[source] ?? source).join(' or ')} just now — affected
            figures are showing as 0, which may not be the real value.
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {isLoading ? (
          <>
            <SkeletonStatTile />
            <SkeletonStatTile />
            <SkeletonStatTile />
            <SkeletonStatTile />
          </>
        ) : isAdmin && report ? (
          <>
            <StatTile icon={Coins} label={`Spend — ${periodLabel}`} value={formatCurrencyUsd(report.org_total.spend_usd)} />
            <StatTile
              icon={Layers3}
              label={`Tokens — ${periodLabel}`}
              value={formatTokenCount(report.org_total.prompt_tokens + report.org_total.completion_tokens)}
            />
            <StatTile icon={Coins} label="Spend (budget cycle)" value={formatCurrencyUsd(report.org_total.spend_usd_cycle)} />
            <StatTile
              icon={AlertTriangle}
              label="Blocked / over budget"
              value={String(blockedOrOverBudgetCount)}
              warn={blockedOrOverBudgetCount > 0}
            />
          </>
        ) : (
          <>
            <StatTile icon={Coins} label="Org spend (budget cycle)" value={formatCurrencyUsd(rollup?.total_spend_usd ?? 0)} />
            <StatTile icon={Users} label="Active members" value={String(rollup?.member_count ?? 0)} />
          </>
        )}
      </div>
    </div>
  );
}
